import * as React from 'react'

import { styled } from '~/styled'

import { TcolorScheme } from '../../../experiments/color-scheme/ColorScheme'
import { Pagination as PaginationComponent } from '../../pagination/Pagination'
import { Text } from '../../text/Text'
import { AsyncDataState } from '../DataTable.types'
import { useDataTable } from '../DataTableContext'

const StyledNav = styled('nav', {
  base: [
    'flex',
    'flex-col',
    'justify-between',
    'items-center',
    'tabular-nums',
    'flex-wrap',
    'w-full',
    'gap-4',
    'mt-4',
    'md:flex-row'
  ]
})

type PaginationProps = React.ComponentProps<typeof StyledNav> & {
  colorScheme?: TcolorScheme
}

/** Applies pagination to parent DataTableProvider and renders UI to switch pages etc */
export const Pagination = ({ colorScheme, ...props }: PaginationProps) => {
  const {
    applyPagination,
    getState,
    getExpandedRowModel,
    getRowModel,
    setPageIndex,
    getTotalRows,
    getPageCount,
    asyncDataState,
    hasAsyncData
  } = useDataTable()

  React.useEffect(() => {
    applyPagination()
  }, [applyPagination])

  const { pagination: paginationState } = getState()
  const isPending = asyncDataState === AsyncDataState.PENDING
  const isEmpty = !isPending && getTotalRows() === 0
  // Show pagination only if total pages are more than 1
  const showPagination = hasAsyncData
    ? getPageCount() > 1
    : getExpandedRowModel().rows.length / paginationState.pageSize > 1

  if (isEmpty || !showPagination) return null
  const totalVisibleRows = getExpandedRowModel().rows.length

  const start =
    totalVisibleRows === 0
      ? 0
      : paginationState.pageIndex * paginationState.pageSize + 1

  const asyncRowCount = getRowModel().rows.length
  const end = hasAsyncData
    ? asyncRowCount === 0
      ? start
      : start + asyncRowCount - 1
    : Math.min(start + paginationState.pageSize - 1, totalVisibleRows)

  // indexing for the pagination component is 1 based
  const setPage = (index: number) => {
    setPageIndex(index - 1)
  }

  const pageCount = hasAsyncData
    ? getPageCount()
    : Math.max(1, Math.ceil(totalVisibleRows / paginationState.pageSize))

  return (
    <StyledNav {...props}>
      <Text size="sm">{`${start} - ${end} of ${
        hasAsyncData ? getTotalRows() : totalVisibleRows
      } items`}</Text>
      <PaginationComponent
        colorScheme={colorScheme}
        selectedPage={paginationState.pageIndex + 1}
        pagesCount={pageCount}
        onSelectedPageChange={setPage}
      />
    </StyledNav>
  )
}

Pagination.displayName = 'Pagination'
